/**
 * LegalReacceptGate — Zero-friction legal version enforcement.
 *
 * On mount (once per app session) checks `/api/legal/consent-status`.
 * If the current user's accepted version is older than the deployed
 * version, displays a blocking modal forcing re-acceptance before
 * they can continue using the app.
 *
 * Non-intrusive for fresh sign-ups (they already accepted the latest
 * version at registration). Only legacy users and version bumps hit this.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { ShieldCheck, ExternalLink, Loader2 } from 'lucide-react';
import { Checkbox } from './ui/checkbox';
import { Button } from './ui/button';
import { api } from '../context/AppContext';
import { LEGAL_DOC_URL } from '../constants/legal';

export default function LegalReacceptGate({ user, language = 'fr' }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const fr = (language || 'fr').toLowerCase().startsWith('fr');

  const fetchStatus = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const res = await api.get('/legal/consent-status');
      setStatus(res.data);
    } catch (e) {
      // Silent fail — don't block the app if endpoint is down
      setStatus({ needs_reaccept: false });
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleAccept = async () => {
    if (!accepted || !status?.current_version) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.post('/legal/accept', { version: status.current_version });
      setStatus({ ...status, needs_reaccept: false });
    } catch (e) {
      setError(e.response?.data?.detail || (fr ? 'Erreur, reessayez.' : 'Error, please retry.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !status || !status.needs_reaccept) return null;

  return (
    <div
      className="fixed inset-0 z-[9998] flex items-end md:items-center justify-center bg-black/80 backdrop-blur-sm p-0 md:p-4"
      data-testid="legal-reaccept-backdrop"
    >
      <div
        className="w-full md:max-w-md bg-neutral-900 text-white rounded-t-3xl md:rounded-3xl shadow-2xl border border-amber-500/40 overflow-hidden max-h-[92vh] flex flex-col"
        data-testid="legal-reaccept-modal"
      >
        <div className="bg-gradient-to-br from-amber-500 via-amber-600 to-amber-800 p-5">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-white/15 rounded-xl">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold leading-tight tracking-tight">
                {fr ? 'Mise a jour des conditions' : 'Terms update'}
              </h2>
              <p className="text-sm text-amber-50/90 mt-0.5">
                {fr
                  ? 'Nos conditions legales ont ete mises a jour.'
                  : 'Our legal terms have been updated.'}
              </p>
            </div>
          </div>
        </div>

        <div className="p-5 space-y-4 text-sm">
          <p className="text-neutral-300 leading-relaxed">
            {fr
              ? "Pour continuer a utiliser YUMIX, merci de relire et d'accepter la nouvelle version de nos CGU, Politique de Confidentialite et Avis de Non-Responsabilite Medicale (incluant la clause de non-divulgation)."
              : 'To continue using YUMIX, please review and accept the new version of our Terms, Privacy Policy, and Medical Disclaimer (including the non-disclosure clause).'}
          </p>

          <div className="bg-neutral-800/60 border border-neutral-700 rounded-xl p-3 text-xs space-y-1">
            <div className="flex justify-between">
              <span className="text-neutral-500">
                {fr ? 'Version acceptee :' : 'Accepted version:'}
              </span>
              <code className="text-neutral-300">
                {status.accepted_version || (fr ? 'aucune' : 'none')}
              </code>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-500">
                {fr ? 'Version actuelle :' : 'Current version:'}
              </span>
              <code className="text-emerald-300">{status.current_version}</code>
            </div>
          </div>

          <a
            href={status.legal_doc_url || LEGAL_DOC_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-amber-300 hover:text-amber-200 font-semibold text-sm underline underline-offset-2"
            data-testid="legal-reaccept-doc-link"
          >
            {fr ? 'Lire la documentation legale complete' : 'Read the full legal documentation'}
            <ExternalLink className="w-4 h-4" />
          </a>

          <label className="flex items-start gap-3 cursor-pointer select-none pt-2">
            <Checkbox
              checked={accepted}
              onCheckedChange={(v) => setAccepted(Boolean(v))}
              className="mt-0.5 border-neutral-500 data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
              data-testid="legal-reaccept-checkbox"
            />
            <span className="text-xs text-neutral-300 leading-relaxed">
              {fr
                ? "Je confirme avoir lu et j'accepte les nouvelles Conditions Generales d'Utilisation (incluant la clause de non-divulgation), la Politique de Confidentialite et l'Avis de Non-Responsabilite Medicale."
                : 'I confirm that I have read and accept the new Terms of Service (including the non-disclosure clause), the Privacy Policy, and the Medical Disclaimer.'}
            </span>
          </label>

          {error && (
            <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg p-2">
              {error}
            </div>
          )}
        </div>

        <div className="border-t border-neutral-700/70 bg-neutral-950/70 p-4">
          <Button
            onClick={handleAccept}
            disabled={!accepted || submitting}
            className="w-full bg-emerald-600 hover:bg-emerald-500 disabled:bg-neutral-700 disabled:text-neutral-500 text-white font-semibold py-6 rounded-xl transition"
            data-testid="legal-reaccept-confirm-btn"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <ShieldCheck className="w-4 h-4 mr-2" />
            )}
            {fr ? "J'accepte et je continue" : 'I accept and continue'}
          </Button>
        </div>
      </div>
    </div>
  );
}
